import { Box, Divider, Grid, Typography, CircularProgress } from '@mui/material'
import { useMemo } from 'react'
import { Modal, Card, IconButton } from 'components'
import { Close } from '@mui/icons-material'
import {
  useLoadProductRawMaterials,
  LoadProductRawMaterialsQuery
} from 'modules/orders/hooks'
import { formatNumber } from 'utils'

type Props = {
  productId: string
  especif1: string
  quantity: number
  open: boolean
  onClose: () => void
}

export const RawMaterialsModal = ({
  productId,
  especif1,
  quantity,
  open,
  onClose
}: Props) => {
  const currentDate = useMemo(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const seconds = String(now.getSeconds()).padStart(2, '0')
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`
  }, [])

  const query: LoadProductRawMaterialsQuery = useMemo(
    () => ({
      especif1,
      quantity,
      currentDate
    }),
    [especif1, quantity, currentDate]
  )

  const { data: rawMaterialsResponse, isLoading } = useLoadProductRawMaterials(
    productId,
    query,
    open && !!especif1 && !!productId
  )

  const rawMaterials = rawMaterialsResponse?.data || []

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}
    >
      <Card
        sx={{
          width: { xs: 350, sm: 600, md: 800 },
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <Box sx={{ p: 2 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem'
            }}
          >
            <Typography variant="h6">Matérias Primas</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </div>

          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200
              }}
            >
              <CircularProgress />
            </Box>
          ) : !rawMaterials?.length ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma matéria prima encontrada para este produto.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={1} sx={{ fontWeight: 'bold', mb: 1 }}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2">Código</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2">Configuração</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2">Descrição</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2">Quantidade</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="subtitle2">Estoque</Typography>
                  </Grid>
                </Grid>
                <Divider />
              </Grid>

              {rawMaterials.map((material: any, index: number) => (
                <Grid
                  item
                  xs={12}
                  key={`${material.productId}-${material.configuration}-${index}`}
                >
                  <Grid container spacing={1} sx={{ py: 1 }}>
                    <Grid item xs={3}>
                      <Typography variant="body2">
                        {material.productId || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2">
                        {material.configuration || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2">
                        {material.description || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2">
                        {formatNumber(material.quantity || 0)}
                        {material.unityMeasure
                          ? ` ${material.unityMeasure}`
                          : ''}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography
                        variant="body2"
                        color={
                          (material.stock || 0) < (material.quantity || 0)
                            ? 'error'
                            : 'text.primary'
                        }
                      >
                        {formatNumber(material.stock || 0)}
                        {material.unityMeasure
                          ? ` ${material.unityMeasure}`
                          : ''}
                      </Typography>
                    </Grid>
                  </Grid>
                  {index < rawMaterials.length - 1 && <Divider />}
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Card>
    </Modal>
  )
}
